import React, { FunctionComponent, ReactNode, useCallback, useMemo } from 'react';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import Translate from 'ui/atoms/translate';
import StatusTag from 'ui/atoms/status-tag';
import { transferStatusToVariant } from 'ui/helper/variant';
import WithDataRecord from 'src/hoc/WithDataRecord';
import DataRecordOrdering from 'src/ui/types/data-ordering';
import { PaginationProps } from 'ui/molecules/pagination';
import {
  AdminApi,
  AdminInvestmentTransfersListOrderingEnum,
  AdminInvestmentTransfersListStatusEnum,
  AdminInvestmentTransfersValuesRetrieveFieldEnum,
  AdminTokenTransfer,
} from 'src/api';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';
import useApiCall from 'src/hooks/use-api-call';

export interface TransactionsTableProps {
  investmentId: string;
  productId: string;
  title?: ReactNode;
  loading?: boolean;
  paginationProps?: PaginationProps;
  defaultLimit?: number;
  defaultOrdering?: DataRecordOrdering;
  forceDataLoad?: boolean;
  setForceDataLoad?: (val: boolean) => void;
}

const TransactionsTable: FunctionComponent<TransactionsTableProps> = WithDataRecord<AdminApi, AdminTokenTransfer>(
  AdminApi,
)(
  ({ data, ordering, onOrderBy, title, loading, paginationProps, investmentId }) => {
    const translate = useTranslateWithStringArgs();
    const { makeAuthenticatedApi } = useApiCall();
    const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

    const transactionStatusFilterOptions = useMemo(() => {
      return Object.values(AdminInvestmentTransfersListStatusEnum).map((status) => ({
        id: status,
        name: translate(`portfolio.transferStatus.${snakeToCamelCaseWithNumbers(status)}`),
      }));
    }, [translate]);

    const getSenderAndReceiverValues = useCallback(
      (options: string[]) => {
        return (
          options
            // Remove options that contain {{BURN}} from the array
            .filter((str) => !str.includes('{{BURN}}'))
            .map((str) => {
              const replacedString = str
                // Replace {{ISSUER}} with proper translation
                .replace('{{ISSUER}}', translate('transferIdentifier.issuance'));
              return {
                value: str,
                label: replacedString,
                name: replacedString,
              };
            })
        );
      },
      [translate],
    );

    return (
      <DataRecordTable
        data={data}
        ordering={ordering}
        onOrderBy={onOrderBy}
        title={title}
        loading={loading}
        paginationProps={paginationProps}
        cells={[
          {
            title: <Translate name="transactionsTable.sender" />,
            render: (transaction) => transaction.investorNameSender,
            orderingFieldName: AdminInvestmentTransfersListOrderingEnum.INVESTOR_NAME_SENDER_ASC,
            type: CellTypeEnum.text,
            filterName: 'senderColumn',
            searchFieldName: AdminInvestmentTransfersValuesRetrieveFieldEnum.SENDER_COLUMN,
            getFilterData: (params: any) =>
              adminApi.adminInvestmentTransfersValuesRetrieve({ ...params, investment: investmentId }),
            processFilterData: (data: string[]) => getSenderAndReceiverValues(data),
          },
          {
            title: <Translate name="transactionsTable.recipient" />,
            render: (transaction) => transaction.investorNameRecipient,
            orderingFieldName: AdminInvestmentTransfersListOrderingEnum.INVESTOR_NAME_RECIPIENT_ASC,
            type: CellTypeEnum.text,
            filterName: 'recipientColumn',
            searchFieldName: AdminInvestmentTransfersValuesRetrieveFieldEnum.RECIPIENT_COLUMN,
            getFilterData: (params: any) =>
              adminApi.adminInvestmentTransfersValuesRetrieve({ ...params, investment: investmentId }),
            processFilterData: (data: string[]) => getSenderAndReceiverValues(data),
          },
          {
            title: <Translate name="portfolio.amount" />,
            alignContent: 'right',
            render: (transaction) => transaction.amount,
            orderingFieldName: AdminInvestmentTransfersListOrderingEnum.AMOUNT_ASC,
            type: CellTypeEnum.number,
            filterName: 'amount',
          },
          {
            title: <Translate name="common.status" />,
            render: (transaction) => (
              <StatusTag variant={transferStatusToVariant(transaction.status)}>
                <Translate name={`portfolio.transferStatus.${snakeToCamelCaseWithNumbers(transaction.status)}`}>
                  {transaction.status}
                </Translate>
              </StatusTag>
            ),
            orderingFieldName: AdminInvestmentTransfersListOrderingEnum.STATUS_ASC,
            type: CellTypeEnum.text,
            filterName: 'status',
            getFilterData: () => {
              return transactionStatusFilterOptions;
            },
          },
        ]}
      />
    );
  },
  (api, props, offset, limit, ordering) => {
    return api.adminInvestmentTransfersList({
      offset,
      limit,
      ordering: ordering as AdminInvestmentTransfersListOrderingEnum,
      investment: props.investmentId,
      ...props,
    });
  },
);

export default TransactionsTable;
